import React, { useState, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import { putMovimiento as modifMovimiento } from "../helpers/rutaMovimientos";
import { getCajas } from "../helpers/rutaCajas";
import { getGastos } from "../helpers/rutaGastos";
import { getComandas } from "../helpers/rutaComandas";
import { getCamiones } from "../helpers/rutaCamiones";
import { getChoferes } from "../helpers/rutaChoferes";
import "animate.css";

const initialForm = {
  fecha: "",
  tipo: "",
  monto: "",
  descripcion: "",
  gasto: "",
  caja: "",
  comanda: "",
  camion: "",
  chofer: "",
  libre: "",
};

const getId = (value) => {
  if (!value) return "";

  if (typeof value === "string") return value;

  if (typeof value === "object") {
    return value._id || value.id || value.$oid || "";
  }

  return "";
};

const norm = (value) =>
  String(value || "")
    .toLowerCase()
    .trim();

const getTextoObjeto = (obj, keys = []) => {
  if (!obj) return "";

  if (typeof obj === "string") return obj;

  for (const key of keys) {
    if (obj?.[key]) return String(obj[key]);
  }

  return "";
};

const resolveIdFromList = (rawValue, list = [], textKeys = []) => {
  const directo = getId(rawValue);

  if (directo && list.some((item) => getId(item) === directo)) {
    return directo;
  }

  const textoRaw = norm(getTextoObjeto(rawValue, textKeys));

  if (!textoRaw) return directo || "";

  const encontrado = list.find((item) => {
    const textoItem = norm(getTextoObjeto(item, textKeys));
    return textoItem && textoItem === textoRaw;
  });

  return getId(encontrado) || directo || "";
};

const toDateTimeLocal = (value) => {
  if (!value) return "";

  const d = new Date(value);

  if (Number.isNaN(d.getTime())) return "";

  const pad = (n) => String(n).padStart(2, "0");

  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const hh = pad(d.getHours());
  const mi = pad(d.getMinutes());

  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
};

const getCuentaBancariaTexto = (movimiento) => {
  const c = movimiento?.cuentaBancaria;

  if (!c) return "";

  if (typeof c === "string") return c;

  return [
    c.descripcion,
    c.nombre,
    c.banco,
    c.alias,
    c.nroCuenta,
    c.cbu,
  ]
    .filter(Boolean)
    .join(" - ");
};

const ModalFormMovimiento = ({ movimiento, handleClose, show }) => {
  const [formValues, setFormValues] = useState(initialForm);

  const [cajas, setCajas] = useState({ data: [], loading: true });
  const [gastos, setGastos] = useState({ data: [], loading: true });
  const [comandas, setComandas] = useState({ data: [], loading: true });
  const [camiones, setCamiones] = useState({ data: [], loading: true });
  const [choferes, setChoferes] = useState({ data: [], loading: true });
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    getCajas().then((res) =>
      setCajas({ data: res.cajas || res.data || [], loading: false })
    );

    getGastos().then((res) =>
      setGastos({ data: res.gastos || res.data || [], loading: false })
    );

    getComandas().then((res) =>
      setComandas({ data: res.comandas || res.data || [], loading: false })
    );

    getCamiones().then((res) =>
      setCamiones({ data: res.camiones || res.data || [], loading: false })
    );

    getChoferes().then((res) =>
      setChoferes({ data: res.choferes || res.data || [], loading: false })
    );
  }, []);

  useEffect(() => {
    if (!movimiento || !movimiento._id) {
      setFormValues(initialForm);
      return;
    }

    setFormValues({
      fecha: toDateTimeLocal(movimiento.fecha),
      tipo: movimiento.tipo || "",
      monto: movimiento.monto || "",
      descripcion: movimiento.descripcion || "",

      caja: resolveIdFromList(movimiento.caja, cajas.data, [
        "descripcion",
        "nombre",
        "caja",
      ]),

      gasto: resolveIdFromList(movimiento.gasto, gastos.data, [
        "gasto",
        "descripcion",
        "nombre",
      ]),

      comanda: resolveIdFromList(movimiento.comanda, comandas.data, [
        "nroviaje",
        "nrodecomanda",
        "codcom",
      ]),

      camion: resolveIdFromList(movimiento.camion, camiones.data, [
        "camion",
        "patente",
      ]),

      chofer: resolveIdFromList(movimiento.chofer, choferes.data, [
        "nombre",
        "chofer",
      ]),

      libre: movimiento.libre || "",
    });
  }, [
    movimiento,
    cajas.data,
    gastos.data,
    comandas.data,
    camiones.data,
    choferes.data,
  ]);

  const cuentaBancariaTexto = getCuentaBancariaTexto(movimiento);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormValues((prev) => ({
      ...prev,
      [name]: name === "descripcion" || name === "libre" ? value.toUpperCase() : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const cleanedValues = { ...formValues };

      // La fecha original del movimiento nunca se modifica
      delete cleanedValues.fecha;

      Object.keys(cleanedValues).forEach((key) => {
        if (cleanedValues[key] === "") {
          delete cleanedValues[key];
        }
      });

      const resp = await modifMovimiento(movimiento._id, cleanedValues);
      console.log("✅ Movimiento actualizado:", resp);

      setShowSuccess(true);

      setTimeout(() => {
        setShowSuccess(false);
        handleClose();
      }, 1500);
    } catch (error) {
      console.error("❌ Error al guardar:", error);
    }
  };

  return (
    <>
      <Modal show={show} onHide={handleClose}>
        <form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>Modificar Movimiento</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <div className="form-group mt-2">
              <label>Fecha</label>
              <input
                type="datetime-local"
                className="form-control"
                name="fecha"
                value={formValues.fecha}
                disabled
              />
            </div>

            <div className="form-group mt-2">
              <label>Tipo</label>
              <select
                className="form-control"
                name="tipo"
                value={formValues.tipo}
                onChange={handleChange}
                required
              >
                <option value="">Seleccione tipo</option>
                <option value="ingreso">Ingreso</option>
                <option value="egreso">Egreso</option>
              </select>
            </div>

            <div className="form-group mt-2">
              <label>Monto</label>
              <input
                type="number"
                className="form-control"
                name="monto"
                required
                value={formValues.monto}
                onChange={handleChange}
              />
            </div>

            {cuentaBancariaTexto && (
              <div className="form-group mt-2">
                <label>Cuenta bancaria del movimiento</label>
                <input
                  type="text"
                  className="form-control"
                  value={cuentaBancariaTexto}
                  disabled
                />
              </div>
            )}

            <div className="form-group mt-2">
              <label>Caja que se asignará</label>
              <select
                className="form-control"
                name="caja"
                value={formValues.caja}
                onChange={handleChange}
              >
                <option value="">Seleccione caja</option>
                {cajas.data.map((c) => (
                  <option key={c._id} value={c._id}>
                    {c.descripcion || c.nombre || c.caja}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group mt-2">
              <label>Tipo de Gasto</label>
              <select
                className="form-control"
                name="gasto"
                value={formValues.gasto}
                onChange={handleChange}
              >
                <option value="">Seleccione gasto</option>
                {gastos.data.map((g) => (
                  <option key={g._id} value={g._id}>
                    {g.gasto || g.descripcion || g.nombre}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group mt-2">
              <label>Nro de Viaje</label>
              <select
                className="form-control"
                name="comanda"
                value={formValues.comanda}
                onChange={handleChange}
              >
                <option value="">Seleccione viaje</option>
                {comandas.data.map((v) => (
                  <option key={v._id} value={v._id}>
                    {v.nroviaje || v.nrodecomanda || v.codcom}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group mt-2">
              <label>Camión</label>
              <select
                className="form-control"
                name="camion"
                value={formValues.camion}
                onChange={handleChange}
              >
                <option value="">Seleccione camión</option>
                {camiones.data.map((c) => (
                  <option key={c._id} value={c._id}>
                    {c.camion || c.patente}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group mt-2">
              <label>Chofer</label>
              <select
                className="form-control"
                name="chofer"
                value={formValues.chofer}
                onChange={handleChange}
              >
                <option value="">Seleccione chofer</option>
                {choferes.data.map((ch) => (
                  <option key={ch._id} value={ch._id}>
                    {ch.nombre || ch.chofer}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group mt-2">
              <label>Descripción</label>
              <textarea
                className="form-control"
                name="descripcion"
                rows={3}
                value={formValues.descripcion}
                onChange={handleChange}
              />
            </div>

            <div className="form-group mt-2">
              <label>Campo Libre</label>
              <textarea
                className="form-control"
                name="libre"
                rows={3}
                value={formValues.libre}
                onChange={handleChange}
              />
            </div>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={handleClose}>
              Cancelar
            </Button>
            <Button type="submit" variant="dark">
              Guardar
            </Button>
          </Modal.Footer>
        </form>
      </Modal>

      <Modal
        show={showSuccess}
        centered
        backdrop="static"
        keyboard={false}
        dialogClassName="animate__animated animate__fadeInDown"
      >
        <Modal.Body className="text-center">
          <div style={{ fontSize: "3rem" }}>🖐</div>
          <h5 className="mt-2">¡Cambios guardados correctamente!</h5>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ModalFormMovimiento;